"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { LayoutGrid, LayoutDashboard, Pencil, Plus, Search, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { categoryHex } from "@/lib/wiki-config";
import { useDemoStore } from "@/stores/demo-store";
import type { DemoLoreCategory } from "@/lib/demo/demo-seed";
import type { GateReason } from "./demo-gate-modal";
import { DemoWikiEntryPanel } from "./demo-wiki-entry-panel";
const CATEGORY_TYPES: Array<{
  value: DemoLoreCategory;
  label: string;
}> = [{
  value: "characters",
  label: "Characters"
}, {
  value: "locations",
  label: "Locations"
}, {
  value: "systems",
  label: "Systems"
}, {
  value: "factions",
  label: "Factions"
}, {
  value: "organizations",
  label: "Organizations"
}, {
  value: "other",
  label: "Other"
}];
export function DemoWiki(t0) {
  const $ = _c(94);
  const {
    onOpenGate
  } = t0;
  const categories = useDemoStore(_temp);
  const lore = useDemoStore(_temp2);
  const storyTitle = useDemoStore(_temp3);
  const addCategory = useDemoStore(_temp4);
  const updateCategory = useDemoStore(_temp5);
  const deleteCategory = useDemoStore(_temp6);
  const addEntry = useDemoStore(_temp7);
  const [query, setQuery] = useState("");
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryType, setCategoryType] = useState("characters");
  const [categoryLabel, setCategoryLabel] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [entryCategoryId, setEntryCategoryId] = useState(null);
  const [entryName, setEntryName] = useState("");
  let t1;
  if ($[0] !== lore || $[1] !== selectedEntryId) {
    let t2;
    if ($[3] !== selectedEntryId) {
      t2 = entry => entry.id === selectedEntryId;
      $[3] = selectedEntryId;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = lore.find(t2);
    $[0] = lore;
    $[1] = selectedEntryId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selected = t1;
  const selectedRootEntryId = selected?.parentEntryId ?? selected?.id ?? null;
  let t2;
  if ($[5] !== lore || $[6] !== query) {
    const normalized = query.trim().toLowerCase();
    t2 = lore.filter(entry_0 => !entry_0.parentEntryId && (!normalized || entry_0.name.toLowerCase().includes(normalized) || entry_0.description.toLowerCase().includes(normalized)));
    $[5] = lore;
    $[6] = query;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const filteredLore = t2;
  let t3;
  if ($[8] !== addCategory || $[9] !== categoryLabel || $[10] !== categoryType) {
    t3 = function createCategory() {
      const fallback = CATEGORY_TYPES.find(option => option.value === categoryType)?.label ?? "Other";
      addCategory(categoryType, categoryLabel.trim() || fallback);
      setCategoryLabel("");
      setShowCategoryForm(false);
    };
    $[8] = addCategory;
    $[9] = categoryLabel;
    $[10] = categoryType;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const createCategory = t3;
  let t4;
  if ($[12] !== categoryLabel || $[13] !== editingCategoryId || $[14] !== updateCategory) {
    t4 = function saveCategoryName() {
      if (!editingCategoryId) {
        return;
      }
      updateCategory(editingCategoryId, categoryLabel);
      setEditingCategoryId(null);
      setCategoryLabel("");
    };
    $[12] = categoryLabel;
    $[13] = editingCategoryId;
    $[14] = updateCategory;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const saveCategoryName = t4;
  let t5;
  if ($[16] !== addEntry || $[17] !== entryCategoryId || $[18] !== entryName) {
    t5 = function createEntry() {
      if (!entryCategoryId || !entryName.trim()) {
        return;
      }
      const id = addEntry(entryCategoryId, entryName);
      setEntryName("");
      setEntryCategoryId(null);
      if (id) {
        setSelectedEntryId(id);
      }
    };
    $[16] = addEntry;
    $[17] = entryCategoryId;
    $[18] = entryName;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const createEntry = t5;
  let t6;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <LayoutDashboard className="size-4 text-primary" />;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== storyTitle) {
    t7 = <h2 className="text-sm font-semibold">{storyTitle} Wiki</h2>;
    $[21] = storyTitle;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== categories.length || $[24] !== lore.length) {
    t8 = <p className="text-[10px] text-muted-foreground">{categories.length} categories · {lore.length} pages</p>;
    $[23] = categories.length;
    $[24] = lore.length;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t7 || $[27] !== t8) {
    t9 = <div className="flex items-center gap-2">{t6}<div>{t7}{t8}</div></div>;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="hidden items-center gap-1 text-xs text-muted-foreground sm:flex"><LayoutGrid className="size-3.5" /> Grid view</div>;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== onOpenGate) {
    t11 = () => onOpenGate("wikiGenerate");
    $[30] = onOpenGate;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  let t13;
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Sparkles />;
    t13 = <span className="sm:hidden">Generate</span>;
    t14 = <span className="hidden sm:inline">Generate Wiki</span>;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
  } else {
    t12 = $[32];
    t13 = $[33];
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t11) {
    t15 = <Button size="sm" variant="outline" onClick={t11} className="min-w-0 flex-1 sm:flex-none">{t12}{t13}{t14}</Button>;
    $[35] = t11;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setShowCategoryForm(true);
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Button size="sm" variant="outline" onClick={t16} className="min-w-0 flex-1 sm:flex-none"><Plus /><span className="sm:hidden">Category</span><span className="hidden sm:inline">Add Category</span></Button>;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t15) {
    t18 = <div className="flex w-full items-center gap-2 sm:w-auto">{t15}{t17}</div>;
    $[39] = t15;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t18 || $[42] !== t9) {
    t19 = <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-card/50 px-3 py-2">{t9}{t10}{t18}</div>;
    $[41] = t18;
    $[42] = t9;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Search className="size-3.5 shrink-0 text-muted-foreground" />;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = event => setQuery(event.target.value);
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== query) {
    t22 = <div className="border-b border-border bg-card/30 px-3 py-2 md:hidden"><div className="flex h-9 items-center gap-2 rounded-md border border-border bg-background px-2">{t20}<input value={query} onChange={t21} placeholder={"Search Wiki\u2026"} className="min-w-0 flex-1 bg-transparent text-sm outline-none" /></div></div>;
    $[46] = query;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Search className="size-3.5 text-muted-foreground" />;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = event_0 => setQuery(event_0.target.value);
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== query) {
    t25 = <div className="mb-3 flex h-8 items-center gap-2 rounded-md border border-border bg-background px-2">{t23}<input value={query} onChange={t24} placeholder={"Search Wiki\u2026"} className="min-w-0 flex-1 bg-transparent text-xs outline-none" /></div>;
    $[50] = query;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== categories || $[53] !== filteredLore) {
    let t27;
    if ($[55] !== filteredLore) {
      t27 = category => <div key={category.id}><p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{category.label}</p>{filteredLore.filter(entry_1 => entry_1.categoryId === category.id).map(entry_2 => <button key={entry_2.id} onClick={() => setSelectedEntryId(entry_2.id)} className="block w-full truncate rounded px-2 py-1 text-left text-xs hover:bg-muted">{entry_2.name}</button>)}</div>;
      $[55] = filteredLore;
      $[56] = t27;
    } else {
      t27 = $[56];
    }
    t26 = categories.map(t27);
    $[52] = categories;
    $[53] = filteredLore;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[57] !== t26) {
    t27 = <div className="space-y-3">{t26}</div>;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t25 || $[60] !== t27) {
    t28 = <aside className="hidden w-56 shrink-0 border-r border-border bg-card/30 p-3 md:block">{t25}{t27}</aside>;
    $[59] = t25;
    $[60] = t27;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== categories || $[63] !== deleteCategory || $[64] !== filteredLore) {
    let t30;
    if ($[66] !== deleteCategory || $[67] !== filteredLore) {
      t30 = category_0 => {
        const entries = filteredLore.filter(entry_3 => entry_3.categoryId === category_0.id);
        return <section key={category_0.id} className="min-w-0 max-w-full overflow-hidden rounded-xl border border-border bg-card p-3 shadow-sm" style={{
          "--demo-category": categoryHex(category_0.category)
        } as React.CSSProperties}><header className="mb-3 flex min-w-0 items-center justify-between gap-2 border-b border-border pb-2"><div className="flex min-w-0 items-center gap-2"><span className="size-2.5 rounded-full bg-[var(--demo-category)]" /><h3 className="truncate text-sm font-semibold">{category_0.label}</h3></div><div className="flex shrink-0 items-center gap-1"><Button size="icon-sm" variant="ghost" onClick={() => setEntryCategoryId(category_0.id)} aria-label={`Add entry to ${category_0.label}`}><Plus /></Button><Button size="icon-sm" variant="ghost" onClick={() => {
                setEditingCategoryId(category_0.id);
                setCategoryLabel(category_0.label);
              }} aria-label={`Rename ${category_0.label}`}><Pencil /></Button><Button size="icon-sm" variant="ghost" onClick={() => deleteCategory(category_0.id)} aria-label={`Delete ${category_0.label}`}><Trash2 /></Button></div></header><div className="space-y-2">{entries.map(entry_4 => <button key={entry_4.id} onClick={() => setSelectedEntryId(entry_4.id)} className="flex w-full min-w-0 max-w-full items-center gap-2 overflow-hidden rounded-lg border border-border bg-background p-2 text-left transition hover:border-[var(--demo-category)]">{entry_4.imageUrl ? <img src={entry_4.imageUrl} alt="" className="size-9 rounded-md object-cover" /> : <span className="flex size-9 items-center justify-center rounded-md bg-[var(--demo-category)] text-xs font-bold text-black">{entry_4.name.slice(0, 2).toUpperCase()}</span>}<span className="min-w-0"><span className="block truncate text-sm font-medium">{entry_4.name}</span><span className="block truncate text-[11px] text-muted-foreground">{entry_4.description || "No description yet"}</span></span></button>)}{entries.length === 0 && <p className="py-5 text-center text-xs text-muted-foreground">No entries yet.</p>}</div></section>;
      };
      $[66] = deleteCategory;
      $[67] = filteredLore;
      $[68] = t30;
    } else {
      t30 = $[68];
    }
    t29 = categories.map(t30);
    $[62] = categories;
    $[63] = deleteCategory;
    $[64] = filteredLore;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[69] !== t29) {
    t30 = <main className="w-full min-w-0 max-w-full flex-1 overflow-x-hidden overflow-y-auto bg-background p-3 sm:p-4"><div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3">{t29}</div></main>;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== onOpenGate || $[72] !== selectedEntryId || $[73] !== selectedRootEntryId) {
    t31 = selectedEntryId && <DemoWikiEntryPanel key={selectedRootEntryId} entryId={selectedEntryId} onClose={() => setSelectedEntryId(null)} onSelectEntry={setSelectedEntryId} onOpenGate={onOpenGate} />;
    $[71] = onOpenGate;
    $[72] = selectedEntryId;
    $[73] = selectedRootEntryId;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== t28 || $[76] !== t30 || $[77] !== t31) {
    t32 = <div className="relative flex min-h-0 flex-1 overflow-hidden">{t28}{t30}{t31}</div>;
    $[75] = t28;
    $[76] = t30;
    $[77] = t31;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  let t33;
  if ($[79] !== categoryLabel || $[80] !== categoryType || $[81] !== createCategory || $[82] !== createEntry || $[83] !== editingCategoryId || $[84] !== entryCategoryId || $[85] !== entryName || $[86] !== saveCategoryName || $[87] !== showCategoryForm) {
    t33 = (showCategoryForm || entryCategoryId || editingCategoryId) && <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4" onClick={() => {
      setShowCategoryForm(false);
      setEntryCategoryId(null);
      setEditingCategoryId(null);
    }}><div role="dialog" aria-modal="true" className="w-full max-w-sm space-y-4 rounded-xl border border-border bg-card p-5" onClick={_temp8}><h3 className="font-semibold">{showCategoryForm ? "New category" : editingCategoryId ? "Rename category" : "New Wiki entry"}</h3>{showCategoryForm ? <><select value={categoryType} onChange={event_2 => setCategoryType(event_2.target.value as DemoLoreCategory)} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">{CATEGORY_TYPES.map(_temp9)}</select><Input value={categoryLabel} onChange={event_3 => setCategoryLabel(event_3.target.value)} placeholder="Custom label (optional)" /><Button className="w-full" onClick={createCategory}>Create category</Button></> : editingCategoryId ? <><Input value={categoryLabel} onChange={event_4 => setCategoryLabel(event_4.target.value)} placeholder="Category name" aria-label="Category name" autoFocus={true} /><Button className="w-full" disabled={!categoryLabel.trim()} onClick={saveCategoryName}>Save category name</Button></> : <><Input value={entryName} onChange={event_5 => setEntryName(event_5.target.value)} placeholder="Entry name" autoFocus={true} /><Button className="w-full" disabled={!entryName.trim()} onClick={createEntry}>Create entry</Button></>}</div></div>;
    $[79] = categoryLabel;
    $[80] = categoryType;
    $[81] = createCategory;
    $[82] = createEntry;
    $[83] = editingCategoryId;
    $[84] = entryCategoryId;
    $[85] = entryName;
    $[86] = saveCategoryName;
    $[87] = showCategoryForm;
    $[88] = t33;
  } else {
    t33 = $[88];
  }
  let t34;
  if ($[89] !== t19 || $[90] !== t22 || $[91] !== t32 || $[92] !== t33) {
    t34 = <div className="flex min-h-0 flex-1 flex-col" data-testid="demo-wiki">{t19}{t22}{t32}{t33}</div>;
    $[89] = t19;
    $[90] = t22;
    $[91] = t32;
    $[92] = t33;
    $[93] = t34;
  } else {
    t34 = $[93];
  }
  return t34;
}
function _temp9(option_0) {
  return <option key={option_0.value} value={option_0.value}>{option_0.label}</option>;
}
function _temp8(event_1) {
  return event_1.stopPropagation();
}
function _temp7(state_5) {
  return state_5.addLoreEntry;
}
function _temp6(state_4) {
  return state_4.deleteLoreCategory;
}
function _temp5(state_3) {
  return state_3.updateLoreCategory;
}
function _temp4(state_2) {
  return state_2.addLoreCategory;
}
function _temp3(state_1) {
  return state_1.storyTitle;
}
function _temp2(state_0) {
  return state_0.lore;
}
function _temp(state) {
  return state.loreCategories;
}